/* eslint-disable max-len */
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StudentActivityReportRequest } from '@models/activity/student-activity-report-request';
import { IAdminBenchmark, IAssessment, IObjective } from '@models/admin-benchmark';
import { ApiResponse } from '@models/api-response';
import { Classroom } from '@models/classroom';
import { ClassroomPin } from '@models/classroom-pin';
import { Paged } from '@models/paged-params';
import { PagedResponse } from '@models/paged-response';
import { School } from '@models/school';
import { StudentActivityReport } from '@models/student-activity-report';
import { StudentProgressReport } from '@models/student-progress-report';
import { StatefulService } from '@services/stateful-service-management/stateful-service-interface/stateful-service-interface';
import {
  BenchmarkSkillsAverageReport,
  SkillsAverageReportRequest
} from '@shared/branded-reports/branded-benchmark-reports/admin-skills-average-report/skills-average-report-detail.model';
import {
  BenchmarkStandardsOverTimeReport,
  BenchmarkStandardsOverTimeRequest, StandardsOverTimeProgressRequest
} from '@shared/branded-reports/branded-benchmark-reports/admin-standards-over-time-report/standards-over-time-report-detail.model';
import {
  BenchmarkClassComparisonReport,
  BenchmarkClassComparisonRequest,
} from '@shared/branded-reports/branded-benchmark-reports/branded-benchmark-class-comparison-detail/class-comparison-report-detail.model';
import {
  ClassOverTimeReport,
  ClassOverTimeReportRequest
} from '@shared/branded-reports/branded-class-over-time-report-detail/class-over-time-report-detail.model';
import { ClassSkillsHeatmapReport, ClassSkillsHeatmapReportRequest } from '@shared/branded-reports/branded-class-skills-heatmap-report-detail/class-skills-heatmap-report-detail.model';
import {
  AdminBenchmarkHeatmapReportRequest,
  ClassSpreadReport,
  ClassSpreadReportRequest
} from '@shared/branded-reports/branded-class-spread-report-detail/class-spread-report-detail.model';
import {
  StudentOverTimeReport,
  StudentOverTimeReportRequest
} from '@shared/branded-reports/branded-individual-student-over-time-report-detail/student-over-time-report-detail.model';
import { StandardsDetailReport, StandardDetailBenchmarkRequest, StandardDetailProgressRequest } from '@shared/branded-reports/shared-standards-detail/shared-standards-detail-report.model';
import { GradeType } from '@shared/enums/grade-type';
import { VariantType } from '@shared/enums/variant-type';
import { Helpers } from '@shared/helpers';
import {
  instantiatePagedResponseFromArrayInterface
} from '@shared/zb-rxjs-operators/rxjs-operators';
import moment from 'moment';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { AppConfigService } from './appconfig.service';

/* eslint-enable max-len */

/** Contains state and API calls. */
@Injectable({
  providedIn: 'root',
})

/*
 * This shim service will allow report-related Observable to span across modules.
 */

export class ReportsService implements StatefulService {
  constructor(
    private http: HttpClient,
    private appConfig: AppConfigService,
  ) {
  }

  scoresReportSelectionCriteria = { schoolId: null, classroomId: null };
  currentSchoolId$ = new BehaviorSubject<string>(null);
  currentClassroomId$ = new BehaviorSubject<string>(null);
  currentStudentId$ = new BehaviorSubject<string>(null);
  currentPinnedRoute$ = new BehaviorSubject<string>(null);
  currentPin$ = new BehaviorSubject<ClassroomPin>(null);
  schools$ = new BehaviorSubject<School[]>([]);
  selectedSchool$ = new BehaviorSubject<School>(null);
  classrooms$ = new BehaviorSubject<Classroom[]>([]);
  selectedClassroom$ = new BehaviorSubject<Classroom>(null);
  studentProgressReports$ = new BehaviorSubject<StudentProgressReport[]>([]);
  selectedStudentProgressReport$ = new BehaviorSubject<StudentProgressReport>(null);

  private previousSchoolId: string = null;
  private previousClassroomId: string = null;

  setCurrentSchoolId$(schoolId: string): void {
    if (schoolId !== this.previousSchoolId) {
      this.previousSchoolId = schoolId;
      this.scoresReportSelectionCriteria.schoolId = schoolId;
      this.currentSchoolId$.next(schoolId);
    }
  }

  setCurrentClassroomId$(classroomId: string): void {
    if (classroomId !== this.previousClassroomId) {
      this.previousClassroomId = classroomId;
      this.scoresReportSelectionCriteria.classroomId = classroomId;
      this.currentClassroomId$.next(classroomId);
    }
  }

  setCurrentStudentId$(studentId: string): void {
    if (studentId !== this.previousClassroomId) {
      this.currentStudentId$.next(studentId);
    }
  }

  setCurrentPinnedRoute$(pinnedRoute: string): void {
    this.currentPinnedRoute$.next(pinnedRoute);
  }

  setCurrentPin$(classroomPin: ClassroomPin): void {
    this.currentPin$.next(classroomPin);
  }

  setSchools$(schools: School[]): void {
    this.schools$.next(schools);
  }

  setSelectedSchool$(school: School): void {
    this.selectedSchool$.next(school);
  }

  setClassrooms$(classrooms: Classroom[]): void {
    this.classrooms$.next(classrooms);
  }

  setSelectedClassroom$(classroom: Classroom): void {
    this.selectedClassroom$.next(classroom);
  }

  setStudentProgressReports$(studentProgressReports: StudentProgressReport[]): void {
    this.studentProgressReports$.next(studentProgressReports);
  }

  setSelectedStudentProgressReport$(studentProgressReport: StudentProgressReport): void {
    this.selectedStudentProgressReport$.next(studentProgressReport);
  }

  resetScoresReportSelectionCriteria() {
    this.scoresReportSelectionCriteria = { schoolId: null, classroomId: null };
  }

  getBenchmark(
    classroomId: string,
    schoolId: string,
    districtId: string,
    schoolYear: string,
    grade: GradeType | string): Observable<ApiResponse<IAdminBenchmark[]>> {
    const params = new HttpParams({
      fromObject: {
        classroomId,
        schoolId,
        districtId,
        schoolYear,
        grade,
      }
    });

    return this.http.get<ApiResponse<IAdminBenchmark[]>>(`${this.appConfig.apiUrl}/report/admin-benchmark`, { params });
  }

  exportAdminBenchmarkReport(
    districtId: string,
    schoolId: string,
    classroomId: string,
    schoolYear: string,
    grade: GradeType): Observable<{ filename: string; data: Blob }> {
    let params = new HttpParams({ fromObject: { schoolYear, grade } });
    if (districtId) {
      params = params.append('districtId', districtId);
    }
    if (schoolId) {
      params = params.append('schoolId', schoolId);
    }
    if (classroomId) {
      params = params.append('classroomId', classroomId);
    }
    const url = `${this.appConfig.apiUrl}/export/report/admin-benchmark`;
    return this.http.get(url, { params, observe: 'response', responseType: 'blob' })
      .pipe(
        map((response: HttpResponse<Blob>) => {
          const contentDisposition = response.headers.get('Content-Disposition');
          // Default name in case we dont get the filename from the API
          let filename = 'benchmark-report.xlsx';
          if (contentDisposition) {
            const matches = contentDisposition.match(/filename="?(.+?)"?$/);
            if (matches && matches[1]) {
              // eslint-disable-next-line prefer-destructuring
              filename = matches[1];
            }
          }
          return {
            filename,
            data: response.body as Blob
          };
        })
      );
  }

  getStudentActivityReport(request: StudentActivityReportRequest) {
    const params = [
      { key: 'userId', value: request.userId },
      { key: 'classroomId', value: request.classroomId },
      { key: 'startDate', value: request.startDate },
      { key: 'endDate', value: request.endDate },
      { key: 'questOnly', value: request.questOnly },
      { key: 'pageNumber', value: request.pageNumber },
      { key: 'pageSize', value: request.pageSize },
      { key: 'includeActivityGames', value: request.includeActivityGames },
      { key: 'includeActivityBooksAndReaders', value: request.includeActivityBooksAndReaders },
      { key: 'includeActivityVideo', value: request.includeActivityVideo },
      { key: 'includeActivityOther', value: request.includeActivityOther },
      { key: 'includeIndependentOnly', value: request.includeIndependentOnly },
      { key: 'needsScoring', value: request.needsScoring },
    ];

    if (request.productType) {
      params.push({ key: 'productType', value: request.productType });
    }

    if (request.grade) {
      params.push({ key: 'grade', value: request.grade });
    }

    if (request.orderBy) {
      params.push({ key: 'orderBy', value: request.orderBy });
    }

    return this.http.get<PagedResponse<StudentActivityReport[]>>(Helpers.buildUrlWithParameters(
      this.appConfig.apiUrl, '/report/classroom/student-activity', params));
  }

  exportStudentActivityReport(request: StudentActivityReportRequest) {
    const params = [
      { key: 'userId', value: request.userId },
      { key: 'classroomId', value: request.classroomId },
      { key: 'startDate', value: request.startDate },
      { key: 'endDate', value: request.endDate },
      { key: 'questOnly', value: request.questOnly },
      { key: 'pageNumber', value: request.pageNumber },
      { key: 'pageSize', value: request.pageSize },
      { key: 'offsetInMinutes', value: moment().utcOffset() },
    ];

    if (request.productType) {
      params.push({ key: 'productType', value: request.productType });
    }

    if (request.grade) {
      params.push({ key: 'grade', value: request.grade });
    }

    if (request.orderBy) {
      params.push({ key: 'orderBy', value: request.orderBy });
    }

    return this.http.get(Helpers.buildUrlWithParameters(
      this.appConfig.apiUrl, '/export/report/classroom/student-activity', params), { responseType: 'blob' });
  }

  getClassOverTimeReport(classroomId: string, request: ClassOverTimeReportRequest): Observable<ApiResponse<ClassOverTimeReport>> {
    const params: HttpParams = Helpers.objToQueryParams(request);
    return this.http.get<any>(
      `${this.appConfig.apiUrl}/report/classroom/${classroomId}/classroom-over-time`,
      { params }
    ).pipe(map(res => new ApiResponse(true, res)));
  }

  exportClassOverTimeReport(classroomId: string, request: ClassOverTimeReportRequest): Observable<HttpResponse<Blob>> {
    const params = {
      ...request,
      classroomId
    };
    return this.http.get(
      `${this.appConfig.apiUrl}/export/report/classroom-over-time`,
      { observe: 'response', params: Helpers.objToQueryParams(params), responseType: 'blob' }
    );
  }

  exportClassSpreadReport(classroomId: string, request: ClassSpreadReportRequest): Observable<HttpResponse<Blob>> {
    const params = {
      ...request,
      classroomId
    };
    return this.http.get(
      `${this.appConfig.apiUrl}/export/report/classroom-spread`,
      { observe: 'response', params: Helpers.objToQueryParams(params), responseType: 'blob' }
    );
  }

  getStudentOverTimeReport(classroomId: string, request: StudentOverTimeReportRequest): Observable<ApiResponse<StudentOverTimeReport>> {
    const params: HttpParams = Helpers.objToQueryParams(request);
    return this.http.get<any>(
      `${this.appConfig.apiUrl}/report/classroom/${classroomId}/student-over-time`,
      { params }
    ).pipe(map(res => new ApiResponse(true, res)));
  }

  exportStudentOverTimeReport(request: StudentOverTimeReportRequest): Observable<HttpResponse<Blob>> {
    const params = {
      ...request,
    };
    return this.http.get(
      `${this.appConfig.apiUrl}/export/report/student-over-time`,
      { observe: 'response', params: Helpers.objToQueryParams(params), responseType: 'blob' }
    );
  }

  getAdminHeatmapBenchmarkReport(request: AdminBenchmarkHeatmapReportRequest): Observable<ApiResponse<IAdminBenchmark[]>> {
    const params: HttpParams = Helpers.objToQueryParams(request);
    return this.http.get<ApiResponse<IAdminBenchmark[]>>(
      `${this.appConfig.apiUrl}/report/admin-benchmark`,
      { params }
    ).pipe(map(res => new ApiResponse(true, res)));
  }

  exportAdminHeatmapBenchmarkReport(request: AdminBenchmarkHeatmapReportRequest): Observable<HttpResponse<Blob>> {
    const params: HttpParams = Helpers.objToQueryParams(request);
    return this.http.get(
      `${this.appConfig.apiUrl}/export/report/admin-benchmark`,
      { observe: 'response', params, responseType: 'blob' }
    );
  }

  exportClassAndUserStatsReport(schoolId: string): Observable<Blob> {
    const params = [
      { key: 'schoolId', value: schoolId },
      { key: 'offsetInMinutes', value: moment().utcOffset() },
    ];
    return this.http.get(Helpers.buildUrlWithParameters(
      this.appConfig.apiUrl, '/export/report/school/student-activity', params), { responseType: 'blob' });
  }

  getClassSpreadReport(classroomId: string, request: ClassSpreadReportRequest): Observable<ApiResponse<ClassSpreadReport>> {
    const params: HttpParams = Helpers.objToQueryParams(request);
    return this.http.get<any>(
      `${this.appConfig.apiUrl}/report/classroom/${classroomId}/classroom-spread`,
      { params }
    ).pipe(map(res => new ApiResponse(true, res)));
  }

  getAssessmentsByProductVariant(productLineKey: string, variant: VariantType): Observable<ApiResponse<IAssessment[]>> {
    return this.http.get<any>(
      `${this.appConfig.apiUrl}/report/product/${productLineKey}/variant/${variant}/selection-list/assessment`,
    ).pipe(map(res => new ApiResponse(true, res)));
  }

  getObjectivesByProductVariant(productLineKey: string, variant: string): Observable<ApiResponse<IObjective[]>> {
    return this.http.get<any>(
      `${this.appConfig.apiUrl}/report/product/${productLineKey}/variant/${variant}/selection-list/objective`,
    ).pipe(map(res => new ApiResponse(true, res)));
  }

  getBenchmarkClassComparisonReport(request: Paged<BenchmarkClassComparisonRequest>): Observable<PagedResponse<BenchmarkClassComparisonReport[]>> {
    const params: HttpParams = Helpers.objToQueryParams(request);
    const url = `${this.appConfig.apiUrl}/report/classroom-comparison`;
    return this.http.get(url, { params }).pipe(
      instantiatePagedResponseFromArrayInterface<BenchmarkClassComparisonReport>()
    );
  }

  getBenchmarkStandardDetailReport(request: Paged<StandardDetailBenchmarkRequest>): Observable<PagedResponse<StandardsDetailReport[]>> {
    const params: HttpParams = Helpers.objToQueryParams(request);
    const url = `${this.appConfig.apiUrl}/report/classroom-standard-detail`;
    return this.http.get<PagedResponse<StandardsDetailReport[]>>(url, { params }).pipe(
      instantiatePagedResponseFromArrayInterface<StandardsDetailReport>()
    );
  }

  exportBenchmarkClassComparisonReport(request: BenchmarkClassComparisonRequest): Observable<Blob> {
    const params: HttpParams = Helpers.objToQueryParams(request);
    const url = `${this.appConfig.apiUrl}/export/report/classroom-comparison`;
    return this.http.get(url, { params, responseType: 'blob' });
  }

  getBenchmarkStandardsOverTimeReport(request: BenchmarkStandardsOverTimeRequest): Observable<ApiResponse<BenchmarkStandardsOverTimeReport>> {
    const params: HttpParams = Helpers.objToQueryParams(request);
    return this.http.get<ApiResponse<BenchmarkStandardsOverTimeReport>>(
      `${this.appConfig.apiUrl}/report/classroom-standard-over-time`,
      { params }
    ).pipe(map(res => new ApiResponse(true, res)));
  }

  getBenchmarkSkillsAverageReport(request: SkillsAverageReportRequest): Observable<PagedResponse<BenchmarkSkillsAverageReport[]>> {
    const params: HttpParams = Helpers.objToQueryParams(request);
    return this.http.get<PagedResponse<BenchmarkSkillsAverageReport[]>>(
      `${this.appConfig.apiUrl}/report/skills-average`,
      { params }
    ).pipe(map(res => new PagedResponse(true, res)));
  }

  getClassSkillsHeatmapReport(classroomId: string, request: ClassSkillsHeatmapReportRequest): Observable<ApiResponse<ClassSkillsHeatmapReport>> {
    const params: HttpParams = Helpers.objToQueryParams(request);
    return this.http.get<any>(
      `${this.appConfig.apiUrl}/report/classroom/${classroomId}/classroom-skills-heatmap`,
      { params }
    ).pipe(map(res => new ApiResponse(true, res)));
  }

  getClassStandardDetailReport(request: Paged<StandardDetailProgressRequest>): Observable<PagedResponse<StandardsDetailReport[]>> {
    const params: HttpParams = Helpers.objToQueryParams(request);
    const url = `${this.appConfig.apiUrl}/report/classroom-standard-detail-teacher`;
    return this.http.get<PagedResponse<StandardsDetailReport[]>>(url, { params }).pipe(
      instantiatePagedResponseFromArrayInterface<StandardsDetailReport>()
    );
  }

  getClassStandardsOverTimeReport(request: StandardsOverTimeProgressRequest): Observable<ApiResponse<BenchmarkStandardsOverTimeReport>> {
    const params: HttpParams = Helpers.objToQueryParams(request);
    return this.http.get<any>(
      `${this.appConfig.apiUrl}/report/classroom-standard-over-time-teacher`,
      { params }
    ).pipe(map(res => new ApiResponse(true, res)));
  }

  exportClassSkillsHeatmapReport(classroomId: string, request: ClassOverTimeReportRequest): Observable<HttpResponse<Blob>> {
    const params = {
      ...request,
      classroomId
    };
    return this.http.get(
      `${this.appConfig.apiUrl}/export/report/classroom-skills-heatmap?`,
      { observe: 'response', params: Helpers.objToQueryParams(params), responseType: 'blob' }
    );
  }

  /** Resets the stateful properties of this service. */
  reset(): void {
    this.scoresReportSelectionCriteria = { schoolId: null, classroomId: null };
    this.currentSchoolId$.next(null);
    this.currentClassroomId$.next(null);
    this.currentStudentId$.next(null);
    this.currentPinnedRoute$.next(null);
    this.currentPin$.next(null);
    this.schools$.next([]);
    this.selectedSchool$.next(null);
    this.classrooms$.next([]);
    this.selectedClassroom$.next(null);
    this.studentProgressReports$.next([]);
    this.selectedStudentProgressReport$.next(null);
    this.previousSchoolId = null;
    this.previousClassroomId = null;
  }
}
